<script setup lang="ts">
/**
 * An odometer: each digit is a column of 0-9 slid by `-d` em behind a one-em
 * window. Counts up from 0 the first time it scrolls into view and follows any
 * later change of `value` (life-layer.md §3 P4).
 *
 * The number stays readable to assistive tech as plain text; the columns are
 * decoration. Under reduced motion the tween hands over the final value at
 * once and the global stylesheet zeroes the column transition.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { reducedMotion } from '@/engine/motion';
import { tweenNumber } from '@/ui/motion/gsap';

import { onEnterView } from './in-view';
import { COUNT_UP_SECONDS, digitCount, digitsOf, FOLLOW_SECONDS, ROLL_MS } from './rolling';

const props = defineProps<{ value: number }>();

const rootEl = ref<HTMLElement | null>(null);
/** What the columns show now: 0 until the figure is in view, then the tween's integer. */
const shown = ref(reducedMotion() ? Math.trunc(props.value) : 0);
const width = computed(() => digitCount(props.value));
const digits = computed(() => digitsOf(shown.value, width.value));

let entered = false;
let cancel: () => void = () => undefined;
let stopWatching: () => void = () => undefined;

function roll(to: number, seconds: number): void {
  cancel();
  cancel = tweenNumber(shown.value, to, (v) => (shown.value = Math.trunc(v)), seconds);
}

onMounted(() => {
  if (!rootEl.value) return;
  stopWatching = onEnterView(rootEl.value, () => {
    entered = true;
    roll(props.value, COUNT_UP_SECONDS);
  });
});

watch(
  () => props.value,
  (value) => {
    if (entered) roll(value, FOLLOW_SECONDS);
  },
);

onBeforeUnmount(() => {
  stopWatching();
  cancel();
});
</script>

<template>
  <span ref="rootEl" class="rolling">
    <span class="sr-only">{{ Math.trunc(value) }}</span>
    <span class="digits" aria-hidden="true">
      <span v-for="(d, i) in digits" :key="i" class="slot">
        <span class="column" :style="{ transform: `translateY(${-d}em)`, transitionDuration: `${ROLL_MS}ms` }">
          <span v-for="n in 10" :key="n" class="cell">{{ n - 1 }}</span>
        </span>
      </span>
    </span>
  </span>
</template>

<style scoped>
.rolling {
  display: inline-block;
  vertical-align: bottom;
}

.digits {
  display: inline-flex;
}

/* A one-em window onto a ten-em column. `ch` keeps the mono face's width. */
.slot {
  display: block;
  height: 1em;
  width: 1ch;
  overflow: hidden;
}

.column {
  display: flex;
  flex-direction: column;
  transition-property: transform;
  transition-timing-function: var(--ease);
}

.cell {
  display: block;
  height: 1em;
  line-height: 1;
}
</style>
